import { isSubProduct } from "@src/routes/projects/$projectId/selections/direct/helpers/subProduct";
import { engine } from "..";
import { getFilterGroupsDefinition } from "../helpers/fieldDefinitions/getFilterGroupsDefinition";
import { FilterGroupsSchema } from "../helpers/schemaHelpers";
import { trackLiveObjects } from "../helpers/trackLiveObjects";
import { withDelete } from "../helpers/withDelete";
import { buildEngineSelectedFilters } from "../technicalSelection/helpers/buildEngineSelectedFilters";
import { buildSuggestion } from "../technicalSelection/helpers/buildSuggestion";
import { buildSuggestionPipe } from "../technicalSelection/helpers/buildSuggestionPipe";
import { buildValidationResult } from "../validationResults/buildValidationResult";
import { toArray } from "../vector";
import { buildProductProperties } from "./buildProductProperties";
import {
  BatchSuggestionArgsOptions,
  initSuggestionArgs,
} from "./suggestionsArgs/initSuggestionArgs";

export type BatchSuggestionInput = {
  dp?: number | null;
  dpBranch?: number | null;
  dpMax?: number | null;
  flow?: number | null;
  kvs?: number | null;
  pipeSize?: string | null;
};

export type FilterGroupsState = {
  filterGroups?: FilterGroupsSchema | null | undefined;
  lastUpdatedFilterId?: string | null | undefined;
};

export type GetBatchSuggestionsFilters = {
  deviceSizes?:
    | {
        sizeAbove: number | null;
        sizeBelow: number | null;
      }
    | null
    | undefined;
  filterGroups?: FilterGroupsSchema | null | undefined;
  lastUpdatedFilterId?: string | null | undefined;
  useFlowOrPower?: "flow" | "power" | null | undefined;
};

type GetBatchSuggestionsArgs = {
  filters: GetBatchSuggestionsFilters;
} & BatchSuggestionArgsOptions;

export const batchSelectionRepository = trackLiveObjects(
  "batchSelectionRepository",
  {
    getFilters(categoryId: string, currentValues?: FilterGroupsState | null) {
      const batchSelection = new engine.BatchSelection();
      const currentSelection = buildEngineSelectedFilters(currentValues);

      const filterGroups = withDelete(
        currentSelection,
        batchSelection.getFilters(categoryId, currentSelection),
      );

      return withDelete(
        batchSelection,
        getFilterGroupsDefinition(filterGroups),
      );
    },
    getProductProperties(categoryId: string) {
      const batchSelection = new engine.BatchSelection();
      return withDelete(
        batchSelection,
        buildProductProperties(batchSelection.getProductProperties(categoryId)),
      );
    },
    getSubProductsTypes(categoryId: string) {
      const batchSelection = new engine.BatchSelection();
      return withDelete(
        batchSelection,
        toArray(batchSelection.getSubProductsTypes(categoryId))
          .map((value) => value.toString())
          .filter(isSubProduct),
      );
    },
    getSuggestionAlternatives(
      input: BatchSuggestionInput,
      options: GetBatchSuggestionsArgs,
    ) {
      const {
        cleanup: cleanupSuggestionArgs,
        inputsVector,
        suggestionArgs,
      } = initSuggestionArgs([input], options);
      const firstInput = inputsVector?.get(0);
      if (!firstInput || !suggestionArgs) {
        cleanupSuggestionArgs();
        return { pipeResults: null, status: "error" as const, suggestions: [] };
      }

      const batchSelection = new engine.BatchSelection();
      const suggestions = batchSelection.getSuggestionAlternatives(
        firstInput,
        suggestionArgs,
      );
      cleanupSuggestionArgs();
      const firstSuggestion = suggestions.get(0);
      return withDelete(batchSelection, {
        pipeResults: firstSuggestion
          ? buildSuggestionPipe(firstSuggestion.getPipeResult())
          : null,
        status: "success" as const,
        suggestions: toArray(suggestions).map((value) =>
          buildSuggestion(value.getSuggestion()),
        ),
      });
    },
    getSuggestions(
      inputs: BatchSuggestionInput[],
      options: GetBatchSuggestionsArgs,
    ) {
      const {
        cleanup: cleanupSuggestionArgs,
        inputsVector,
        suggestionArgs,
      } = initSuggestionArgs(inputs, options);
      if (!inputsVector || !suggestionArgs) {
        cleanupSuggestionArgs();
        return { status: "error" as const, suggestions: [] };
      }

      const batchSelection = new engine.BatchSelection();
      const suggestions = batchSelection.getSuggestions(
        inputsVector,
        suggestionArgs,
      );

      cleanupSuggestionArgs();
      return withDelete(batchSelection, {
        status: "success" as const,
        suggestions: toArray(suggestions).map((value) => ({
          pipeResults: buildSuggestionPipe(value.getPipeResult()),
          suggestion: buildSuggestion(value.getSuggestion()),
          validationResult: buildValidationResult(value.getValidationResult()),
        })),
      });
    },
  },
);
